import { NgClass, NgStyle } from '@angular/common';
import { Component, effect, input, signal } from '@angular/core';
import { RiskOnCreation, RiskScoreStandardized } from '@shared/types';
import { HlmCardModule } from '@spartan-ng/card';
import { RiskIndicatorBarComponent } from './components/risk-indicator-bar/risk-indicator-bar.component';
import { RiskLevelsLegendComponent } from './components/risk-levels-legend.component';
import { RiskLevelsOverviewComponent } from './components/risk-levels-overview.component';

/**
 * @description
 * A reusable component for displaying risk indicators that works with both customer cases and customers.
 * Handles the display and processing of risk scores with consistent UI presentation.
 */
@Component({
  selector: 'dxc-risk-indicators',
  imports: [
    NgClass,
    NgStyle,
    RiskLevelsOverviewComponent,
    RiskIndicatorBarComponent,
    RiskLevelsLegendComponent,
    HlmCardModule
  ],
  template: `
    <section
      hlmCard
      [ngClass]="{ 'risk-indicators': inPdf() }"
      [ngStyle]="getSectionStyles()"
    >
      <div
        hlmCardHeader
        class="gap-y-2 pb-0"
        direction="column"
        [ngStyle]="getPdfPaddingStyles()"
      >
        <h3 hlmCardTitle>Risk Indicators</h3>
        <dxc-risk-levels-overview
          [inPdf]="inPdf()"
          [loading]="loading()"
          [overallRisk]="overallRisk()"
          [riskLevel]="riskLevel()"
          [type]="type()"
          [weightedAverage]="weightedAverage()"
        />
        @if (riskOnCreation()?.visible) {
          <h4>Risk on creation</h4>
          <dxc-risk-levels-overview
            [inPdf]="inPdf()"
            [isRiskOnCreation]="true"
            [loading]="loading()"
            [overallRisk]="riskOnCreation()?.overall_risk_dxc_calc ?? ''"
            [riskLevel]="riskOnCreation()?.customer_risk_level_dxc_calc ?? ''"
            [type]="type()"
            [weightedAverage]="
              riskOnCreation()?.weighted_average_dxc_calc ?? ''
            "
          />
        }
      </div>
      <div hlmCardContent [ngStyle]="getPdfPaddingStyles()">
        <dxc-risk-levels-legend [loading]="loading()" />
        <div class="risk-indicator-bars flex flex-col gap-y-4.5">
          @for (
            risk of loading() ? loadingBars() : processedRiskScores();
            track $index
          ) {
            <dxc-risk-indicator-bar
              [ngClass]="{ 'loading-div': loading() }"
              [risk]="risk"
            />
          }
        </div>
      </div>
    </section>
  `
})
export class RiskIndicatorsComponent {
  public type = input<'case' | 'customer'>('case');
  public loading = input.required<boolean>();
  public inPdf = input<boolean>(false);
  public riskLevel = input.required<string>();
  public weightedAverage = input.required<string>();
  public overallRisk = input.required<string>();
  public riskScores = input.required<RiskScoreStandardized[]>();
  public riskOnCreation = input<RiskOnCreation>();
  protected readonly loadingBars = signal<RiskScoreStandardized[]>(
    Array(5)
      .fill(null)
      .map(() => ({ type: 'loading', label: 'loading', score: 0 }))
  );

  /**
   * @description
   * Computes the processed risk scores based on the input risk scores.
   */
  public processedRiskScores = signal<RiskScoreStandardized[]>([]);

  constructor() {
    effect(() => this.processedRiskScores.set(this.getProcessedRiskScores()));
  }

  /**
   * @description
   * Defines the types of scores that require special processing.
   */
  private readonly SPECIAL_SCORE_TYPES = [
    'pep',
    'blacklistWatchlistSanctions',
    'sanctions',
    'watchlist'
  ] as const;

  /**
   * @description
   * Defines the types of scores that should be excluded from processing.
   */
  private readonly EXCLUDED_SCORE_TYPES = [
    'pepDxcCalc',
    'pepAnalystIntervention',
    'blacklistWatchlistSanctionsDxcCalc',
    'blacklistWatchlistSanctionsAnalystIntervention',
    'sanctionsDxcCalc',
    'sanctionsAnalystIntervention',
    'watchlistDxcCalc',
    'watchlistAnalystIntervention'
  ];

  /**
   * @description
   * Computes the processed risk scores.
   */
  private getProcessedRiskScores(): RiskScoreStandardized[] {
    return this.riskScores()
      .filter(this.filterScores)
      .map((score) => this.processScore(score, this.riskScores()));
  }

  /**
   * @description
   * Filters out excluded score types and scores that are null or zero.
   */
  private filterScores = (score: RiskScoreStandardized): boolean =>
    !this.EXCLUDED_SCORE_TYPES.includes(score.type)
    && score.score !== null
    && score.score !== undefined
    && score.score !== 0;

  /**
   * @description
   * Processes an individual score, applying special processing for certain score types.
   */
  private processScore(
    score: RiskScoreStandardized,
    allScores: RiskScoreStandardized[]
  ): RiskScoreStandardized {
    if (!this.SPECIAL_SCORE_TYPES.includes(score.type as any)) {
      return score;
    }

    let prefix: string;
    switch (score.type) {
      case 'pep':
        prefix = 'pep';
        break;
      case 'blacklistWatchlistSanctions':
        prefix = 'blacklistWatchlistSanctions';
        break;
      case 'sanctions':
        prefix = 'sanctions';
        break;
      case 'watchlist':
        prefix = 'watchlist';
        break;
      default:
        return score;
    }

    return this.processSpecialScore(score, allScores, prefix);
  }

  /**
   * @description
   * Applies special processing for scores that have DXC Calc and Analyst Intervention variants.
   */
  private processSpecialScore(
    score: RiskScoreStandardized,
    allScores: RiskScoreStandardized[],
    prefix: string
  ): RiskScoreStandardized {
    const [dxcCalcScore, analystInterventionScore] = this.getRelatedScores(
      allScores,
      prefix
    );

    if (this.shouldUseDxcCalcScore(analystInterventionScore, dxcCalcScore)) {
      return { ...score, score: dxcCalcScore ?? null };
    } else {
      return { ...score, dxcCalcScore };
    }
  }

  /**
   * @description
   * Retrieves the DXC Calc and Analyst Intervention scores for a given prefix.
   */
  private getRelatedScores(
    allScores: RiskScoreStandardized[],
    prefix: string
  ): [number | null | undefined, number | null | undefined] {
    return [
      allScores.find((s) => s.type === `${prefix}DxcCalc`)?.score,
      allScores.find((s) => s.type === `${prefix}AnalystIntervention`)?.score
    ];
  }

  private shouldUseDxcCalcScore(
    analystInterventionScore: number | null | undefined,
    dxcCalcScore: number | null | undefined
  ): boolean {
    return (
      analystInterventionScore === null
      && dxcCalcScore !== null
      && dxcCalcScore !== undefined
    );
  }

  /**
   * Returns the styles for the main section element based on PDF mode
   */
  protected getSectionStyles(): Record<string, string> {
    if (!this.inPdf()) {
      return {};
    }

    return { border: 'none', 'box-shadow': 'none' };
  }

  /**
   * Returns common padding styles for PDF mode elements
   */
  protected getPdfPaddingStyles(): Record<string, string> {
    if (!this.inPdf()) {
      return {};
    }

    return { padding: '0' };
  }
}
